<template>
  <public-layout
    badge="Community Stories"
    title="Blog &amp; News"
    subtitle="Industry insights, project spotlights, and the latest from Africa's construction &amp; real estate community."
  >
    <!-- ═══════ FEATURED POST ═══════ -->
    <section v-if="featured.length" class="sc-section sc-blog-featured">
      <div class="sc-container">
        <div class="sc-section-header">
          <div><h2>Featured</h2><p>Hand-picked highlights from the community</p></div>
        </div>
        <a :href="'/blog/' + featured[0].slug" class="featured-card">
          <div class="featured-card__image">
            <img
              v-if="blogImage(featured[0])"
              :src="blogImage(featured[0])"
              :alt="featured[0].title"
              @error="onImgError"
            />
            <div v-else class="featured-card__image-placeholder"></div>
            <span class="featured-card__badge">Featured</span>
          </div>
          <div class="featured-card__body">
            <span v-if="featured[0].category" class="tag">{{ featured[0].category.name }}</span>
            <h2>{{ featured[0].title }}</h2>
            <p class="featured-card__date">{{ formatDate(featured[0].created_at) }}</p>
            <span class="btn-outline">Read article</span>
          </div>
        </a>
      </div>
    </section>

    <!-- ═══════ ALL POSTS ═══════ -->
    <section class="sc-section sc-blog-grid" :class="{ 'no-top-padding': featured.length }">
      <div class="sc-container">
        <div class="sc-section-header">
          <div><h2>Latest Articles</h2><p>Stay up to date with our newest posts</p></div>
        </div>

        <div v-if="loading" class="blog-grid">
          <div v-for="n in 6" :key="n" class="blog-card-skeleton">
            <div class="skeleton" style="aspect-ratio:16/9;border-radius:var(--radius);margin-bottom:1rem;"></div>
            <div class="skeleton" style="height:14px;width:60px;border-radius:20px;margin-bottom:.75rem;"></div>
            <div class="skeleton" style="height:20px;width:90%;border-radius:4px;margin-bottom:.5rem;"></div>
            <div class="skeleton" style="height:20px;width:70%;border-radius:4px;margin-bottom:1rem;"></div>
            <div class="skeleton" style="height:13px;width:40%;border-radius:4px;"></div>
          </div>
        </div>

        <div v-else-if="posts.length" class="blog-grid">
          <a
            v-for="post in posts"
            :key="post.id"
            :href="'/blog/' + post.slug"
            class="blog-card"
          >
            <div class="blog-card__image">
              <img
                v-if="blogImage(post)"
                :src="blogImage(post)"
                :alt="post.title"
                @error="onImgError"
              />
              <div v-else class="blog-card__image-placeholder"></div>
            </div>
            <div class="blog-card__body">
              <span v-if="post.category" class="tag">{{ post.category.name }}</span>
              <h3>{{ post.title }}</h3>
              <p class="blog-card__date">{{ formatDate(post.created_at) }}</p>
            </div>
          </a>
        </div>

        <div v-else class="sc-empty-state">
          <div class="sc-empty-state__icon">📰</div>
          <h3>No articles yet</h3>
          <p>Check back soon for new content.</p>
        </div>

        <!-- Pagination -->
        <div v-if="lastPage > 1" class="blog-pagination">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="fetchPosts(prevUrl)"
          >&#8592; Previous</button>
          <span class="page-info">Page {{ currentPage }} of {{ lastPage }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === lastPage"
            @click="fetchPosts(nextUrl)"
          >Next &#8594;</button>
        </div>
      </div>
    </section>
  </public-layout>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'Blog',
  components: { PublicLayout },

  data() {
    return {
      posts: [],
      featured: [],
      loading: true,
      currentPage: 1,
      lastPage: 1,
      prevUrl: null,
      nextUrl: null,
    };
  },

  created() {
    useSEO({
      title: 'Blog & Industry News',
      description: 'Stay updated with the latest construction and real estate news, insights, and project spotlights from Africa\'s growing construction community.',
      url: SITE_ORIGIN + '/blog',
    });
    this.fetchPosts();
    this.fetchFeatured();
  },

  methods: {
    fetchPosts(url = process.env.VUE_APP_API_URL + 'blog/all') {
      this.loading = true;
      axios(url)
        .then(res => {
          if (res.status === 200) {
            this.posts       = res.data.data || [];
            this.currentPage = res.data.current_page || 1;
            this.lastPage    = res.data.last_page || 1;
            this.prevUrl     = res.data.prev_page_url;
            this.nextUrl     = res.data.next_page_url;
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    fetchFeatured() {
      axios(process.env.VUE_APP_API_URL + 'blog/featured')
        .then(res => { if (res.status === 200) this.featured = Array.isArray(res.data) ? res.data : [res.data]; })
        .catch(() => {});
    },

    blogImage(post) {
      if (post.images && post.images.length) return post.images[0].path;
      if (post.image) return post.image;
      return null;
    },
    formatDate(d) { return moment(d).format('MMMM DD, YYYY'); },
    onImgError(e) { e.target.style.display = 'none'; },
  },
};
</script>

<style scoped>
.sc-blog-featured { background: var(--surface); padding-bottom: 0; }
.sc-blog-grid { background: var(--surface-2); }
.sc-blog-grid.no-top-padding { padding-top: 3rem; }

/* Featured card */
.featured-card {
  display: grid; grid-template-columns: 1fr 1fr; gap: 0;
  border-radius: var(--radius-lg); overflow: hidden;
  background: var(--surface); box-shadow: var(--shadow-lg);
  text-decoration: none; color: inherit;
  transition: box-shadow var(--transition), transform var(--transition);
}
.featured-card:hover { box-shadow: var(--shadow-xl); transform: translateY(-2px); text-decoration: none; color: inherit; }
.featured-card__image { position: relative; aspect-ratio: 4/3; overflow: hidden; background: var(--surface-3); }
.featured-card__image img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s ease; }
.featured-card:hover .featured-card__image img { transform: scale(1.04); }
.featured-card__image-placeholder { width: 100%; height: 100%; background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%); }
.featured-card__badge {
  position: absolute; top: 1rem; left: 1rem;
  background: var(--accent); color: var(--primary-dark);
  font-size: .75rem; font-weight: 700; padding: .3rem .75rem;
  border-radius: var(--radius-full); text-transform: uppercase; letter-spacing: .04em;
}
.featured-card__body {
  padding: 2.5rem; display: flex; flex-direction: column; justify-content: center; gap: 1rem;
}
.featured-card__body h2 {
  font-size: 1.625rem; font-weight: 700; color: var(--text);
  margin: 0; line-height: 1.25; letter-spacing: -.02em;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.featured-card__date { font-size: .875rem; color: var(--text-muted); margin: 0; }

/* Tag badge */
.tag {
  display: inline-block; padding: .25rem .75rem;
  background: rgba(30,58,95,.08); color: var(--primary);
  border-radius: var(--radius-full); font-size: .78rem; font-weight: 600;
}

/* Blog grid */
.blog-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem; }

.blog-card {
  background: var(--surface); border-radius: var(--radius-lg);
  overflow: hidden; box-shadow: var(--shadow-sm); border: 1px solid var(--border-light);
  text-decoration: none; color: inherit; display: flex; flex-direction: column;
  transition: transform var(--transition), box-shadow var(--transition);
}
.blog-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); text-decoration: none; color: inherit; }
.blog-card__image { aspect-ratio: 16/9; overflow: hidden; background: var(--surface-3); }
.blog-card__image img { width: 100%; height: 100%; object-fit: cover; transition: transform .35s ease; }
.blog-card:hover .blog-card__image img { transform: scale(1.04); }
.blog-card__image-placeholder { width: 100%; height: 100%; background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%); opacity: .7; }
.blog-card__body { padding: 1.25rem; flex: 1; display: flex; flex-direction: column; gap: .625rem; }
.blog-card__body h3 {
  font-size: 1rem; font-weight: 600; color: var(--text); margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.blog-card__date { font-size: .8125rem; color: var(--text-muted); margin-top: auto; }

/* Skeleton */
.blog-card-skeleton { background: var(--surface); border-radius: var(--radius-lg); padding: 1.25rem; box-shadow: var(--shadow-sm); }
.skeleton { background: linear-gradient(90deg, var(--border-light) 25%, var(--surface-3) 50%, var(--border-light) 75%); background-size: 200% 100%; animation: shimmer 1.4s ease infinite; }
@keyframes shimmer { to { background-position: -200% 0; } }

/* Pagination */
.blog-pagination { display: flex; align-items: center; justify-content: center; gap: 1.5rem; margin-top: 3rem; }
.page-btn {
  padding: .5rem 1.25rem; border: 2px solid var(--border); border-radius: var(--radius-full);
  background: var(--surface); color: var(--text); font-family: var(--font); font-size: .9rem; font-weight: 500;
  cursor: pointer; transition: border-color .15s, background .15s;
}
.page-btn:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-info { font-size: .9rem; color: var(--text-muted); }

/* Empty state */
.sc-empty-state { text-align: center; padding: 4rem 2rem; }
.sc-empty-state__icon { font-size: 3rem; margin-bottom: 1rem; }
.sc-empty-state h3 { font-size: 1.25rem; font-weight: 700; color: var(--text); margin: 0 0 .5rem; }
.sc-empty-state p { color: var(--text-muted); margin: 0; }

@media (max-width: 768px) {
  .featured-card { grid-template-columns: 1fr; }
  .featured-card__image { aspect-ratio: 16/9; }
}
</style>
